import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Plus, ChevronRight, CheckCircle2, AlertTriangle, MapPin, X, ChevronDown, ChevronUp, ShieldCheck, Flag, Shirt, Monitor, GitCompare, Clock, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useOffline } from "@/context/OfflineContext";
import { isThisWeek, formatPhone, normalizePhoneForSave, isAddressValid, formatVisitDate } from "@/lib/utils";
import AddressFields from "@/components/AddressFields";
import SharingTreeEmailBatchModal from "@/components/SharingTreeEmailBatchModal";
import db from "@/lib/db";
import { queueOp } from "@/lib/sync";
import { hasClientPrivacy, hiddenClientFields } from "@/lib/client-privacy";
import { getPrivacySession, privacySignature } from "@/lib/privacy-session";
import ClientOutreachFilters from "@/components/ClientOutreachFilters";
import SharingTreeOutreachBadge from "@/components/SharingTreeOutreachBadge";

function isCoatSeason() {
  const m = new Date().getMonth() + 1;
  return m >= 10 || m <= 3;
}

const PAGE_SIZE = 50;

export default function Clients() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { isOnline } = useOffline();
  const isViewer = user?.role === 'viewer';
  const profileReadOnly = isViewer || hasClientPrivacy(user);
  // The URL is the single source of list state, including Back/Forward.
  const q = searchParams.get("search") || "";
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const parsedPage = parseInt(searchParams.get("page") || "1", 10);
  const page = parsedPage > 0 ? parsedPage : 1;
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const activeFilter = searchParams.get("filter") || null;
  const emailVisible = !hiddenClientFields(user).includes("email");
  const hasEmail = emailVisible ? searchParams.get("has_email") || "" : "";
  const visitYear = searchParams.get("last_visit_year") || "";
  const visitMonth = searchParams.get("last_visit_month") || "";
  const outreachStage = searchParams.get("sharing_tree_stage") || "";
  const [programYear, setProgramYear] = useState(null);
  const [loadError, setLoadError] = useState("");
  const requestSequence = useRef(0);
  const [filterCounts, setFilterCounts] = useState({ flagged: 0, visited_month: 0, visited_week: 0 });
  const [offlineMode, setOfflineMode] = useState(false);
  const sortBy = searchParams.get("sort_by") || "name";
  const sortDir = searchParams.get("sort_dir") || "asc";
  const isOnlineRef = useRef(isOnline);
  useEffect(() => { isOnlineRef.current = isOnline; }, [isOnline]);
  useEffect(() => {
    if (!emailVisible && searchParams.has("has_email")) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("has_email");
        return next;
      }, { replace: true });
    }
  }, [emailVisible, searchParams, setSearchParams]);

  // Kiosk state
  const [kioskClientId, setKioskClientId] = useState(null);

  // Bulk selection state
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [signupEmailOpen, setSignupEmailOpen] = useState(false);

  function changeCriteria(values) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(values).forEach(([key, value]) => {
        if (value) next.set(key, value); else next.delete(key);
      });
      next.delete("page");
      return next;
    }, { replace: true });
  }
  const setQ = (value) => changeCriteria({ search: value });
  const setActiveFilter = (value) => changeCriteria({
    filter: typeof value === "function" ? value(activeFilter) : value,
  });
  function changePage(nextPage) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextPage === 1) next.delete("page"); else next.set("page", nextPage);
      return next;
    }, { replace: true });
  }

  const load = useCallback(async (query = "", pageNum = 1, filter = "", sBy = "name", sDir = "asc") => {
    const sequence = ++requestSequence.current;
    const current = () => sequence === requestSequence.current;
    setLoading(true);
    setLoadError("");
    const skip = (pageNum - 1) * PAGE_SIZE;

    if (!isOnlineRef.current) {
      setProgramYear(null);
      setOfflineMode(true);
      setClients([]);
      setTotal(0);
      setFilterCounts({ flagged: 0, visited_month: 0, visited_week: 0 });
      if (filter || hasEmail || visitYear || visitMonth || outreachStage) {
        setClients([]);
        setTotal(0);
        setOfflineMode(true);
        setLoading(false);
        setLoadError("Connect to the internet to use client filters. Cached visits and email history may be incomplete.");
        return;
      }
      if (hasClientPrivacy(user)) {
        setClients([]);
        setTotal(0);
        setOfflineMode(true);
        setLoading(false);
        return;
      }
      try {
        const all = await db.clients.toArray();
        if (!current()) return;
        const qLower = query.toLowerCase();
        const filtered = all.filter((c) => {
          if (!qLower) return true;
          const name = `${c.first_name || ""} ${c.last_name || ""} ${c.spouse_first_name || ""}`.toLowerCase();
          const phone = (c.phone || "").toLowerCase();
          const email = (c.email || "").toLowerCase();
          const address = (c.address || "").toLowerCase();
          const members = (c.household_members || []).map((m) => `${m.first_name || ""} ${m.last_name || ""}`).join(" ").toLowerCase();
          return name.includes(qLower) || phone.includes(qLower) || email.includes(qLower) || address.includes(qLower) || members.includes(qLower);
        });
        const sorted = filtered.sort((a, b) => {
          let va = a[sBy] ?? "";
          let vb = b[sBy] ?? "";
          if (typeof va === "string") va = va.toLowerCase();
          if (typeof vb === "string") vb = vb.toLowerCase();
          if (va < vb) return sDir === "asc" ? -1 : 1;
          if (va > vb) return sDir === "asc" ? 1 : -1;
          return 0;
        });
        const page = sorted.slice(skip, skip + PAGE_SIZE);
        setClients(page.map((c) => ({ ...c, id: c.id || c._id, sharing_tree_outreach: { stage: "unavailable" } })));
        setTotal(sorted.length);
        setOfflineMode(true);
      } catch { if (current()) { setLoadError("The cached client list could not be loaded."); } }
      finally { if (current()) setLoading(false); }
      return;
    }

    try {
      const { data } = await api.get(
        `/clients?search=${encodeURIComponent(query)}&skip=${skip}&limit=${PAGE_SIZE}&filter=${encodeURIComponent(filter)}&sort_by=${encodeURIComponent(sBy)}&sort_dir=${encodeURIComponent(sDir)}${hasEmail ? `&has_email=${hasEmail}` : ""}${visitYear ? `&last_visit_year=${encodeURIComponent(visitYear)}` : ""}${visitMonth ? `&last_visit_month=${encodeURIComponent(visitMonth)}` : ""}${outreachStage ? `&sharing_tree_stage=${encodeURIComponent(outreachStage)}` : ""}`
      );
      if (!current()) return;
      setClients(data.clients);
      setTotal(data.total);
      setProgramYear(data.sharing_tree_year || null);
      if (data.filter_counts) setFilterCounts(data.filter_counts);
      setOfflineMode(false);

      if (!hasClientPrivacy(user) && privacySignature(user) === privacySignature(getPrivacySession()) &&
          data.clients && data.clients.length > 0) {
        const records = data.clients.map((c) => ({ ...c, _id: c.id }));
        try {
          await db.clients.bulkPut(records);
          await db.syncMeta.put({ key: "clients_last_synced", value: new Date().toISOString() });
        } catch {
          if (current()) setLoadError("The online list is current, but its offline copy could not be updated.");
        }
      }
    } catch (err) {
      if (current()) {
        setClients([]); setTotal(0); setProgramYear(null);
        setFilterCounts({ flagged: 0, visited_month: 0, visited_week: 0 });
        setLoadError(formatApiError(err));
      }
    } finally { if (current()) setLoading(false); }
  }, [user, hasEmail, visitYear, visitMonth, outreachStage]);

  useEffect(() => {
    const timer = setTimeout(() => load(q, page, activeFilter ?? "", sortBy, sortDir), 250);
    return () => { clearTimeout(timer); requestSequence.current += 1; };
  }, [q, page, activeFilter, sortBy, sortDir, load, isOnline]);

  useEffect(() => {
    api.get("/kiosk").then(({ data }) => {
      setKioskClientId(data.visible && data.client_id ? data.client_id : null);
    }).catch(() => {});
  }, []);

  const flaggedCount = filterCounts.flagged;
  const visitedMonthCount = filterCounts.visited_month;
  const visitedWeekCount = filterCounts.visited_week;

  // Filtering is now fully server-side; the backend returns only matching
  // records for the active pill so no client-side filtering is needed.
  const visible = clients;

  const visibleIds = visible.map((c) => c.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0;

  function toggleFilter(f) {
    setActiveFilter((prev) => (prev === f ? null : f));
  }

  function toggleRow(id, e) {
    e.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll(e) {
    e.stopPropagation();
    if (allVisibleSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.add(id));
        return next;
      });
    }
  }

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }

  function handleSort(col) {
    const newDir = sortBy === col ? (sortDir === "asc" ? "desc" : "asc") : "asc";
    changeCriteria({ sort_by: col, sort_dir: newDir });
  }

  async function handleBulkVerify() {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/clients/bulk-verify", { client_ids: [...selected] });
      setSelected(new Set());
      await load(q, page, activeFilter ?? "", sortBy, sortDir);
      showToast(`${data.updated} client${data.updated !== 1 ? "s" : ""} marked verified`);
    } catch (err) {
      showToast(formatApiError(err));
    } finally { setBulkBusy(false); }
  }

  async function handleBulkClearFlag() {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/clients/bulk-clear-flag", { client_ids: [...selected] });
      setSelected(new Set());
      await load(q, page, activeFilter ?? "", sortBy, sortDir);
      showToast(`${data.updated} client${data.updated !== 1 ? "s" : ""} flag${data.updated !== 1 ? "s" : ""} cleared`);
    } catch (err) {
      showToast(formatApiError(err));
    } finally { setBulkBusy(false); }
  }

  return (
    <div className="space-y-6 pb-28" data-testid="clients-page">
      <div className="flex items-start sm:items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: "#8B918B" }}>
            Client Records
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "Outfit" }}>Clients</h1>
          <p className="text-sm mt-2" style={{ color: "var(--fb-text-muted)" }}>
            Search by name, household member (e.g. spouse), address, phone, or email.
          </p>
        </div>
        {!profileReadOnly && (
          <button
            data-testid="new-client-btn"
            onClick={() => setShowNew(true)}
            className="fb-btn-primary px-4 sm:px-5 py-3 rounded-xl font-semibold inline-flex items-center gap-2 text-sm sm:text-base"
          >
            <Plus className="w-4 h-4" /> New Client
          </button>
        )}
      </div>

      <div className="fb-card p-5">
        <div className="relative">
          <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2" style={{ color: "#8B918B" }} />
          <input
            data-testid="client-search-input"
            aria-label="Search clients"
            autoFocus
            placeholder="Search by name, household member, phone, email, or address…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full pl-12 pr-4 py-4 rounded-xl border-2 outline-none focus:border-[#052F66] transition-all text-base"
            style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
          />
        </div>

        <ClientOutreachFilters
          hasEmail={hasEmail} visitYear={visitYear} visitMonth={visitMonth}
          stage={outreachStage} programYear={programYear}
          emailVisible={emailVisible}
          quickActive={!!activeFilter}
          onChange={changeCriteria}
          onClear={() => changeCriteria({ has_email: "", last_visit_year: "", last_visit_month: "", sharing_tree_stage: "", filter: "" })}
        >
          {(flaggedCount > 0 || activeFilter === "flagged") && (
            <FilterPill
              testid="filter-flagged-btn"
              active={activeFilter === "flagged"}
              count={flaggedCount}
              label="Needs review"
              icon={<AlertTriangle className="w-3.5 h-3.5" />}
              activeStyle={{ background: "var(--fb-primary)", color: "white" }}
              inactiveStyle={{ background: "var(--fb-warning-bg)", color: "var(--fb-warning)" }}
              onClick={() => toggleFilter("flagged")}
            />
          )}
          {(visitedMonthCount > 0 || activeFilter === "month") && (
            <FilterPill
              testid="filter-month-btn"
              active={activeFilter === "month"}
              count={visitedMonthCount}
              label="Visited this month"
              activeStyle={{ background: "var(--fb-primary)", color: "white" }}
              inactiveStyle={{ background: "#E0F2FE", color: "#0369A1" }}
              onClick={() => toggleFilter("month")}
            />
          )}
          {(visitedWeekCount > 0 || activeFilter === "week") && (
            <FilterPill
              testid="filter-week-btn"
              active={activeFilter === "week"}
              count={visitedWeekCount}
              label="Visited this week"
              activeStyle={{ background: "var(--fb-primary)", color: "white" }}
              inactiveStyle={{ background: "#E0F2FE", color: "#0369A1" }}
              onClick={() => toggleFilter("week")}
            />
          )}
        </ClientOutreachFilters>
      </div>
      {loadError && <div role="alert" className="fb-status-warning p-3 rounded-xl text-sm">{loadError}</div>}

      {offlineMode && (
        <div
          data-testid="offline-cache-note"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium"
          style={{ background: "#F1F5F9", color: "#475569", border: "1px solid #CBD5E1" }}
        >
          <Clock className="w-4 h-4 shrink-0" />
          {activeFilter || hasEmail || visitYear || visitMonth || outreachStage
            ? "Client filters are unavailable offline — connect to load matching clients."
            : "Showing cached client list — results may not include recent changes"}
        </div>
      )}

      <div className="fb-card overflow-hidden" aria-busy={loading}>
        <PaginationBar
          total={total}
          page={page}
          pageSize={PAGE_SIZE}
          onPrev={() => {
            changePage(page - 1);
          }}
          onNext={() => {
            changePage(page + 1);
          }}
          onPageChange={(next) => {
            changePage(next);
          }}
          borderBottom
        />
        <div className="overflow-x-auto">
          <table className="w-full min-w-[740px]">
            <thead>
              <tr style={{ background: "var(--fb-bg)" }}>
                <th className="px-4 py-4 w-10">
                  <input
                    data-testid="select-all-checkbox"
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded cursor-pointer accent-[#052F66]"
                    title={allVisibleSelected ? "Deselect all" : "Select all visible"}
                  />
                </th>
                <SortableHeader label="Name" col="name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <th className="text-left text-xs uppercase tracking-wider font-semibold px-4 py-4" style={{ color: "var(--fb-text-muted)" }}>Contact</th>
                <SortableHeader label="Address" col="address" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Household" col="household_size" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Status" col="verified" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <th className="text-left text-xs uppercase tracking-wider font-semibold px-4 py-4" style={{ color: "var(--fb-text-muted)" }}>Sharing Tree{programYear ? ` ${programYear}` : ""}</th>
                <th className="px-4 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && !loading && (
                <tr>
                   <td colSpan={8} className="px-6 py-16 text-center text-sm" style={{ color: "var(--fb-text-muted)" }}>
                    {hasEmail || visitYear || visitMonth ? "No clients match these filters." :
                     activeFilter === "flagged" ? "No flagged clients." :
                     activeFilter === "month" ? "No clients visited this month." :
                     activeFilter === "week" ? "No clients visited this week." :
                     "No clients match your search. Try a different keyword."}
                  </td>
                </tr>
              )}
              {visible.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`client-row-${c.id}`}
                  className="border-t cursor-pointer hover:bg-stone-50 transition-colors"
                  style={{
                    borderColor: "var(--fb-border)",
                    background: selected.has(c.id) ? "#F0F4FF" : undefined,
                  }}
                  onClick={() => navigate(`/staff/clients/${c.id}`)}
                >
                  <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                    <input
                      data-testid={`select-${c.id}`}
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={(e) => toggleRow(c.id, e)}
                      className="w-4 h-4 rounded cursor-pointer accent-[#052F66]"
                    />
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold" style={{ color: "var(--fb-text)" }}>
                        {c.spouse_first_name
                          ? `${c.first_name} & ${c.spouse_first_name} ${c.last_name}`
                          : `${c.first_name} ${c.last_name}`}
                      </span>
                      {c._pendingSync && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                          style={{ background: "#FEF3C7", color: "#92400E" }}>
                          <Clock className="w-3 h-3" /> Pending sync
                        </span>
                      )}
                      {kioskClientId === c.id && (
                        <span
                          data-testid={`kiosk-badge-${c.id}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                          style={{ background: "#DBEAFE", color: "#1D4ED8" }}
                        >
                          <Monitor className="w-3 h-3" /> On Kiosk
                        </span>
                      )}
                    </div>
                    {c.matched_members && c.matched_members.length > 0 && (
                      <div className="text-xs mt-0.5 flex items-center gap-1" style={{ color: "var(--fb-primary)" }} data-testid={`matched-member-${c.id}`}>
                        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "var(--fb-primary)" }} />
                        Matched: {c.matched_members.map((m) => `${m.first_name} ${m.last_name}`.trim() + (m.relationship ? ` (${m.relationship})` : "")).join(", ")}
                      </div>
                    )}
                    {c.last_visit_date && (
                      <div className="text-xs mt-0.5" style={{ color: "var(--fb-text-muted)" }}>
                        Last visit: {formatVisitDate(c.last_visit_date)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 text-sm" style={{ color: "var(--fb-text)" }}>
                    <div>{c.phone ? formatPhone(c.phone) : "—"}</div>
                    <div className="text-xs" style={{ color: "var(--fb-text-muted)" }}>{c.email || ""}</div>
                  </td>
                  <td className="px-4 py-4 text-sm" style={{ color: "var(--fb-text)" }}>
                    <div className="flex items-start gap-1.5">
                      {c.address_verified ? (
                        <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--fb-success)" }} />
                      ) : (
                        <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "#8B918B" }} />
                      )}
                      <span className="line-clamp-2">{c.address || "—"}</span>
                      {c.google_address_verified && <span data-testid={`google-verified-${c.id}`} className="shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "#EFF6FF", color: "#1E40AF" }}>Google verified</span>}
                    </div>
                  </td>
                  <td className="px-4 py-4 tabular-nums font-medium text-sm">{c.household_size}</td>
                  <td className="px-4 py-4">
                    <div className="flex flex-col items-start gap-1">
                      {isThisWeek(c.last_visit_date) ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: "#EDE9FE", color: "#5B21B6" }}>
                          <AlertTriangle className="w-3 h-3" />
                          Visited this week
                        </span>
                      ) : c.visited_this_month ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold fb-status-warning">
                          <AlertTriangle className="w-3 h-3" />
                          Visited this month
                        </span>
                      ) : c.address_verified ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold fb-status-success">
                          <CheckCircle2 className="w-3 h-3" />
                          Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold fb-status-error">
                          Unverified
                        </span>
                      )}
                      {(c.import_flags || []).includes("possible_duplicate") && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold fb-status-warning" data-testid={`flag-duplicate-${c.id}`}>
                          <AlertTriangle className="w-3 h-3" /> Possible duplicate
                        </span>
                      )}
                      {(c.import_flags || []).includes("possible_member_duplicate") && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold fb-status-warning" data-testid={`flag-member-dup-${c.id}`}>
                          <AlertTriangle className="w-3 h-3" /> Duplicate children
                        </span>
                      )}
                      {(c.import_flags || []).includes("incomplete_phone") && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "#F0E9DB", color: "#6B5A1E" }} data-testid={`flag-phone-${c.id}`}>
                          Incomplete phone
                        </span>
                      )}
                      {(c.import_flags || []).includes("incomplete_address") && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "#F0E9DB", color: "#6B5A1E" }} data-testid={`flag-addr-${c.id}`}>
                          Incomplete address
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <SharingTreeOutreachBadge status={c.sharing_tree_outreach} />
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {isCoatSeason() && c.coat_count_this_season > 0 && (
                        <span
                          data-testid={`coat-badge-${c.id}`}
                          title={`${c.coat_count_this_season} coat record${c.coat_count_this_season !== 1 ? "s" : ""} this season`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                          style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}
                        >
                          <Shirt className="w-3 h-3" /> {c.coat_count_this_season}
                        </span>
                      )}
                      <ChevronRight className="w-4 h-4" style={{ color: "var(--fb-text-muted)" }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <PaginationBar
          total={total}
          page={page}
          pageSize={PAGE_SIZE}
          onPrev={() => {
            changePage(page - 1);
          }}
          onNext={() => {
            changePage(page + 1);
          }}
          onPageChange={(next) => {
            changePage(next);
          }}
          borderTop
        />
      </div>

      {/* Floating action bar — shown when at least one row is selected */}
      {someSelected && (
        <div
          data-testid="bulk-action-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-5 py-3 rounded-2xl shadow-2xl"
          style={{ background: "#052F66", color: "white", minWidth: 320 }}
        >
          <span className="text-sm font-semibold tabular-nums flex-1">
            {selected.size} selected
          </span>
          {selected.size === 2 && (
            <button
              data-testid="bulk-compare-btn"
              disabled={bulkBusy}
              onClick={() => {
                const [idA, idB] = [...selected];
                navigate(`/staff/clients/compare?a=${idA}&b=${idB}`);
              }}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold transition-colors disabled:opacity-50"
              style={{ background: "#6366f1", color: "white" }}
            >
              <GitCompare className="w-3.5 h-3.5" />
              Compare
            </button>
          )}
          {!profileReadOnly && (
            <button
              data-testid="bulk-sharing-tree-email-btn"
              disabled={bulkBusy || !programYear || offlineMode}
              onClick={() => setSignupEmailOpen(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold transition-colors disabled:opacity-50"
              style={{ background: "#0EA5E9", color: "white" }}
            >
              <Mail className="w-3.5 h-3.5" />
              Notify signup open
            </button>
          )}
          {!profileReadOnly && (
            <button
              data-testid="bulk-verify-btn"
              disabled={bulkBusy}
              onClick={handleBulkVerify}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold transition-colors disabled:opacity-50"
              style={{ background: "#22c55e", color: "white" }}
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              Mark verified
            </button>
          )}
          {!profileReadOnly && (
            <button
              data-testid="bulk-clear-flag-btn"
              disabled={bulkBusy}
              onClick={handleBulkClearFlag}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold transition-colors disabled:opacity-50"
              style={{ background: "#f59e0b", color: "white" }}
            >
              <Flag className="w-3.5 h-3.5" />
              Clear dup flag
            </button>
          )}
          <button
            data-testid="bulk-cancel-btn"
            disabled={bulkBusy}
            onClick={() => setSelected(new Set())}
            className="p-1.5 rounded-lg hover:bg-white/10"
            aria-label="Cancel selection"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Toast confirmation */}
      {toast && (
        <div
          data-testid="bulk-toast"
          className="fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl shadow-xl text-sm font-medium"
          style={{ background: "#052F66", color: "white" }}
        >
          {toast}
        </div>
      )}

      {showNew && <NewClientModal isOnline={isOnline} onClose={() => setShowNew(false)} onCreated={(data) => { load(q, 1, "", sortBy, sortDir); navigate(`/staff/clients/${data.id}?new=1`); }} />}
      {signupEmailOpen && (
        <SharingTreeEmailBatchModal
          mode="signup_open"
          year={programYear}
          recipientIds={[...selected]}
          onClose={() => setSignupEmailOpen(false)}
          onComplete={(data) => {
            showToast(`${data.sent} signup-open email${data.sent !== 1 ? "s" : ""} sent`);
            load(q, page, activeFilter ?? "", sortBy, sortDir);
          }}
        />
      )}
    </div>
  );
}

function SortableHeader({ label, col, sortBy, sortDir, onSort }) {
  const isActive = sortBy === col;
  return (
    <th className="text-left text-xs uppercase tracking-wider font-semibold px-4 py-4">
      <button
        onClick={() => onSort(col)}
        className="inline-flex items-center gap-1 transition-colors hover:opacity-80"
        style={{ color: isActive ? "var(--fb-primary)" : "var(--fb-text-muted)" }}
      >
        {label}
        <span className="flex flex-col leading-none ml-0.5">
          <ChevronUp className="w-2.5 h-2.5" style={{ opacity: isActive && sortDir === "asc" ? 1 : 0.3 }} />
          <ChevronDown className="w-2.5 h-2.5" style={{ opacity: isActive && sortDir === "desc" ? 1 : 0.3 }} />
        </span>
      </button>
    </th>
  );
}

function getPageNumbers(page, totalPages) {
  const delta = 2;
  const pages = new Set();
  pages.add(1);
  pages.add(totalPages);
  for (let i = Math.max(2, page - delta); i <= Math.min(totalPages - 1, page + delta); i++) {
    pages.add(i);
  }
  const sorted = Array.from(pages).sort((a, b) => a - b);
  const result = [];
  let prev = null;
  for (const p of sorted) {
    if (prev !== null && p - prev > 1) result.push("...");
    result.push(p);
    prev = p;
  }
  return result;
}

function PaginationBar({ total, page, pageSize, onPrev, onNext, onPageChange, borderTop, borderBottom }) {
  if (total <= pageSize) return null;
  const totalPages = Math.ceil(total / pageSize);
  const pageNumbers = getPageNumbers(page, totalPages);
  return (
    <div
      data-testid="pagination-bar"
      className="flex items-center justify-between px-5 py-3 flex-wrap gap-2"
      style={{
        borderColor: "var(--fb-border)",
        borderTopWidth: borderTop ? 1 : 0,
        borderBottomWidth: borderBottom ? 1 : 0,
        borderStyle: "solid",
      }}
    >
      <span className="text-sm" style={{ color: "var(--fb-text-muted)" }}>
        {total} result{total !== 1 ? "s" : ""} — Page {page} of {totalPages}
      </span>
      <div className="flex items-center gap-1">
        <button
          data-testid="pagination-prev"
          disabled={page <= 1}
          onClick={onPrev}
          className="px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-stone-50"
          style={{ borderColor: "var(--fb-border)" }}
        >
          ← Prev
        </button>
        {pageNumbers.map((item, idx) =>
          item === "..." ? (
            <span
              key={`ellipsis-${idx}`}
              className="px-2 py-1.5 text-sm select-none"
              style={{ color: "var(--fb-text-muted)" }}
            >
              …
            </span>
          ) : (
            <button
              key={item}
              data-testid={`pagination-page-${item}`}
              onClick={() => onPageChange(item)}
              className="w-9 py-1.5 rounded-lg border text-sm font-medium transition-colors hover:bg-stone-50"
              style={{
                borderColor: item === page ? "var(--fb-green)" : "var(--fb-border)",
                background: item === page ? "var(--fb-green)" : undefined,
                color: item === page ? "#fff" : undefined,
                cursor: item === page ? "default" : undefined,
              }}
              disabled={item === page}
            >
              {item}
            </button>
          )
        )}
        <button
          data-testid="pagination-next"
          disabled={page >= totalPages}
          onClick={onNext}
          className="px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-stone-50"
          style={{ borderColor: "var(--fb-border)" }}
        >
          Next →
        </button>
      </div>
    </div>
  );
}

function FilterPill({ label, count, active, onClick, icon, activeStyle, inactiveStyle, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      aria-pressed={!!active}
      onClick={onClick}
      className="inline-flex items-center gap-1.5 min-h-[44px] sm:min-h-[36px] px-3 py-1.5 rounded-full text-xs font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#052F66]"
      style={active ? activeStyle : inactiveStyle}
    >
      {icon}
      {label} ({count})
      {active && <X className="w-3 h-3 ml-0.5" aria-hidden="true" />}
    </button>
  );
}

function NewClientModal({ onClose, onCreated, isOnline }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "", address: "",
    gender: "", age: "",
    address_verified: false,
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function save(e) {
    e.preventDefault();
    setError(""); setSaving(true);
    if (!isAddressValid(form.address)) {
      setError("Address is incomplete. Please fill in Street, City, State, and a 5-digit Zip.");
      setSaving(false);
      return;
    }
    try {
      const { age, ...rest } = form;
      const payload = { ...rest, household_members: [] };
      const ageNum = parseInt(age);
      if (!isNaN(ageNum)) {
        payload.birth_year = new Date().getFullYear() - ageNum;
        payload.birth_month = 1;
      }

      if (!isOnline) {
        // A short-lived proof cannot survive an offline sync queue.
        delete payload.google_verification_token;
        const tempId = `pending_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const localRecord = {
          ...payload,
          _id: tempId,
          id: tempId,
          household_size: 1,
          household_members: [],
          visited_this_month: false,
          address_verified: false,
          import_flags: [],
          _pendingSync: true,
        };
        await db.clients.put(localRecord);
        await queueOp("create", { ...payload, _id: tempId });
        onCreated({ id: tempId });
        return;
      }

      const { data } = await api.post("/clients", payload);
      const serverRecord = { ...data, _id: data.id };
      await db.clients.put(serverRecord);
      onCreated(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-6 bg-black/40 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg my-4 p-6 sm:p-7" onClick={(e) => e.stopPropagation()} data-testid="new-client-modal">
        <div className="flex items-start justify-between mb-6">
          <h3 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>New Client</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="First Name" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} required testid="new-firstname" />
            <Field label="Last Name" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} required testid="new-lastname" />
          </div>
          <Field label="Phone" value={formatPhone(form.phone)} onChange={(v) => setForm({ ...form, phone: v })} onBlur={() => setForm((prev) => ({ ...prev, phone: normalizePhoneForSave(prev.phone) }))} testid="new-phone" />
          <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" testid="new-email" />
          <AddressFields value={form.address} onChange={(v) => setForm((f) => ({ ...f, address: v }))} onVerification={(t) => setForm((f) => ({ ...f, google_verification_token: t || "" }))} compact />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1.5">Gender</label>
              <select
                data-testid="new-gender"
                value={form.gender}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
                className="w-full px-3.5 py-2.5 rounded-lg border outline-none bg-white"
                style={{ borderColor: "var(--fb-border)" }}
              >
                <option value="">— Select —</option>
                <option>Female</option>
                <option>Male</option>
                <option>Non-binary</option>
                <option>Prefer not to say</option>
              </select>
            </div>
            <Field label="Age" value={form.age} onChange={(v) => setForm({ ...form, age: v })} type="number" testid="new-age" />
          </div>

          {error && <div className="text-sm fb-status-error p-3 rounded-lg">{error}</div>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-lg border" style={{ borderColor: "var(--fb-border)" }}>Cancel</button>
            <button data-testid="save-new-client-btn" type="submit" disabled={saving} className="fb-btn-primary px-5 py-2.5 rounded-lg font-semibold">
              {saving ? "Saving…" : "Create Client"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, onBlur, required, type = "text", testid }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">{label}{required && <span style={{ color: "var(--fb-error)" }}> *</span>}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className="w-full px-3.5 py-2.5 rounded-lg border outline-none focus:border-[#052F66] transition-all"
        style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
      />
    </div>
  );
}
